"use client";

import Image from "next/image";
import { memo, type ReactNode, useEffect, useRef, useState } from "react";
import { cn } from "../../../lib";
import { PulseFloatMotion } from "../../custom/pulse-float-motion";
import {
	SCOPED_PANEL_MAP_SPOTLIGHT_CHARACTER_PIN_Z_INDEX,
	SCOPED_PANEL_MAP_SPOTLIGHT_CHARACTER_Z_INDEX,
	SCOPED_PANEL_SPOTLIGHT_DATA_ATTR,
	scopedPanelSpotlightClassName,
} from "../../scoped-panel/constants";
import {
	type CharacterMovementDisplaySnapshot,
	resolveCharacterRenderCenter,
} from "../hooks/use-hexagon-map-character-movement";
import { shortestFacingDeltaRad } from "../lib/hex-character-facing";
import { resolveHexagonCharacterFeetNudgeYPx } from "../lib/hexagon-layout-metrics";
import {
	HEXAGON_MAP_OVERLAY_FLOAT_DURATION_S,
	resolveMapPlayerPinGapAboveCharacterPx,
} from "../lib/map-overlay-gap";

/** Above character body (z-40); below Radix tooltips. */
const HEXAGON_MAP_PLAYER_PIN_Z_INDEX = 41;

/** Zoom-agnostic pin width (counter-scaled via parent `scale(1/contentMapScale)`). */
const HEXAGON_MAP_PLAYER_PIN_WIDTH_PX = 53;
const HEXAGON_MAP_PLAYER_PIN_HEIGHT_PX = 70;

/** Idle facing: re-render React/WebGL yaw when turned ~1°+. */
const FACING_REACT_PUBLISH_EPS_RAD = 0.02;

export interface HexagonMapPlayerCharacterRenderArgs {
	display: CharacterMovementDisplaySnapshot;
	center: { left: number; top: number };
	stopOverlaySize: number;
	contentMapScale: number;
	userZoom: number;
	playerPinSrc?: string | null;
	isScopedPanelSpotlight?: boolean;
	hidePlayerPin?: boolean;
	layoutMotionPhase?: "stable" | "exit" | "enter";
	layoutMotionDurationMs?: number;
}

interface HexagonMapPlayerCharacterHostProps {
	subscribeDisplay: (listener: () => void) => () => void;
	getDisplaySnapshot: () => CharacterMovementDisplaySnapshot;
	restingCenter: { left: number; top: number } | null;
	isVisible: boolean;
	stopOverlaySize: number;
	contentPositionTransitionMs: number;
	contentMapScale: number;
	userZoom: number;
	playerPinSrc?: string | null;
	/** Elevates the existing character above the scoped panel dim overlay. */
	isScopedPanelSpotlight?: boolean;
	/** Hides the player pin while the scoped preview is open. */
	hidePlayerPin?: boolean;
	/**
	 * Layout seed / path refresh motion — same scale-fade out→in as map tiles.
	 * `stable` uses normal position transitions.
	 */
	layoutMotionPhase?: "stable" | "exit" | "enter";
	layoutMotionDurationMs?: number;
	renderPlayerCharacter: (
		args: HexagonMapPlayerCharacterRenderArgs,
	) => ReactNode;
}

interface CoarseDisplaySlice {
	isMoving: boolean;
	speedScale: number;
	playerIcon: string;
	facingRad: number;
	stepKey: string;
	hasCenter: boolean;
	centerLeft: number;
	centerTop: number;
}

function resolveCoarseSlice(
	display: CharacterMovementDisplaySnapshot,
	restingCenter: { left: number; top: number } | null,
): CoarseDisplaySlice {
	const center = resolveCharacterRenderCenter(display, restingCenter);
	const from = display.activeStepFrom;
	const to = display.activeStepTo;
	return {
		isMoving: display.isMoving,
		speedScale: display.speedScale ?? 0,
		playerIcon: display.playerIcon,
		facingRad: display.facingRad ?? 0,
		stepKey:
			from && to
				? `${from.row},${from.col}->${to.row},${to.col}`
				: display.isMoving
					? "moving"
					: "idle",
		hasCenter: center != null,
		centerLeft: center?.left ?? 0,
		centerTop: center?.top ?? 0,
	};
}

function coarseSliceChanged(
	prev: CoarseDisplaySlice,
	next: CoarseDisplaySlice,
): boolean {
	if (prev.hasCenter !== next.hasCenter) {
		return true;
	}
	if (prev.isMoving !== next.isMoving) {
		return true;
	}
	if (prev.playerIcon !== next.playerIcon) {
		return true;
	}
	if (prev.stepKey !== next.stepKey) {
		return true;
	}
	if (Math.abs(prev.speedScale - next.speedScale) > 0.02) {
		return true;
	}
	const facingDelta = Math.abs(
		shortestFacingDeltaRad(prev.facingRad, next.facingRad),
	);
	if (next.isMoving && facingDelta > 0.001) {
		return true;
	}
	if (!next.isMoving && facingDelta > FACING_REACT_PUBLISH_EPS_RAD) {
		return true;
	}
	return false;
}

export const HexagonMapPlayerCharacterHost = memo(
	function HexagonMapPlayerCharacterHost({
		subscribeDisplay,
		getDisplaySnapshot,
		restingCenter,
		isVisible,
		stopOverlaySize,
		contentPositionTransitionMs,
		contentMapScale,
		userZoom,
		playerPinSrc,
		isScopedPanelSpotlight = false,
		hidePlayerPin = false,
		layoutMotionPhase = "stable",
		layoutMotionDurationMs = 260,
		renderPlayerCharacter,
	}: HexagonMapPlayerCharacterHostProps) {
		const characterHostRef = useRef<HTMLDivElement>(null);
		const pinHostRef = useRef<HTMLDivElement>(null);
		const restingCenterRef = useRef(restingCenter);
		restingCenterRef.current = restingCenter;
		const stopOverlaySizeRef = useRef(stopOverlaySize);
		stopOverlaySizeRef.current = stopOverlaySize;
		const feetNudgeYRef = useRef(
			resolveHexagonCharacterFeetNudgeYPx(stopOverlaySize),
		);
		feetNudgeYRef.current =
			resolveHexagonCharacterFeetNudgeYPx(stopOverlaySize);
		const userZoomRef = useRef(userZoom);
		userZoomRef.current = userZoom;
		const coarseRef = useRef<CoarseDisplaySlice>(
			resolveCoarseSlice(getDisplaySnapshot(), restingCenter),
		);
		const [coarse, setCoarse] = useState(coarseRef.current);

		/**
		 * High-frequency: imperative left/top (no React).
		 * Low-frequency: coarse slice → React → 3D clip/yaw props.
		 * Avoid useSyncExternalStore here — snapshot identity changes every
		 * mover frame and would re-render the WebGL slot continuously.
		 */
		useEffect(() => {
			const applyVisual = () => {
				const display = getDisplaySnapshot();
				const center = resolveCharacterRenderCenter(
					display,
					restingCenterRef.current,
				);
				if (center) {
					const feetTop = center.top + feetNudgeYRef.current;
					const el = characterHostRef.current;
					if (el) {
						el.style.left = `${center.left}px`;
						el.style.top = `${feetTop}px`;
					}
					const pin = pinHostRef.current;
					if (pin) {
						const gap = resolveMapPlayerPinGapAboveCharacterPx(
							userZoomRef.current,
						);
						/** Feet below tile center → box grows upward (scene pin). */
						const characterTop =
							feetTop - stopOverlaySizeRef.current;
						pin.style.left = `${center.left}px`;
						pin.style.top = `${characterTop - gap}px`;
					}
				}
				const next = resolveCoarseSlice(
					display,
					restingCenterRef.current,
				);
				if (coarseSliceChanged(coarseRef.current, next)) {
					coarseRef.current = next;
					setCoarse(next);
				}
			};
			applyVisual();
			return subscribeDisplay(applyVisual);
		}, [getDisplaySnapshot, subscribeDisplay]);

		useEffect(() => {
			const display = getDisplaySnapshot();
			const next = resolveCoarseSlice(display, restingCenter);
			if (coarseSliceChanged(coarseRef.current, next)) {
				coarseRef.current = next;
				setCoarse(next);
			}
			if (!next.hasCenter) {
				return;
			}
			const feetNudgeY =
				resolveHexagonCharacterFeetNudgeYPx(stopOverlaySize);
			const feetTop = next.centerTop + feetNudgeY;
			const el = characterHostRef.current;
			if (el && !display.isMoving) {
				el.style.left = `${next.centerLeft}px`;
				el.style.top = `${feetTop}px`;
			}
			const pin = pinHostRef.current;
			if (pin && !display.isMoving) {
				const gap = resolveMapPlayerPinGapAboveCharacterPx(userZoom);
				const characterTop = feetTop - stopOverlaySize;
				pin.style.left = `${next.centerLeft}px`;
				pin.style.top = `${characterTop - gap}px`;
			}
		}, [getDisplaySnapshot, restingCenter, stopOverlaySize, userZoom]);

		if (!isVisible || !coarse.hasCenter) {
			return null;
		}

		const center = {
			left: coarse.centerLeft,
			top: coarse.centerTop,
		};
		const feetNudgeY = resolveHexagonCharacterFeetNudgeYPx(stopOverlaySize);
		const feetTop = center.top + feetNudgeY;

		const inverseScale = contentMapScale > 0 ? 1 / contentMapScale : 1;
		const gapAboveAnchorPx =
			resolveMapPlayerPinGapAboveCharacterPx(userZoom);
		/** Feet below tile center; sprite box grows up (scene pattern). */
		const characterTop = feetTop - stopOverlaySize;
		const pinAnchorTop = characterTop - gapAboveAnchorPx;

		const isLayoutMotion = layoutMotionPhase !== "stable";
		const positionTransition = isLayoutMotion
			? "none"
			: coarse.isMoving
				? "opacity 300ms cubic-bezier(.4,2,.35,1)"
				: `left ${contentPositionTransitionMs}ms cubic-bezier(.4,2,.35,1), top ${contentPositionTransitionMs}ms cubic-bezier(.4,2,.35,1), opacity 300ms cubic-bezier(.4,2,.35,1)`;

		const layoutMotionClass =
			layoutMotionPhase === "exit"
				? "sq-hex-map-character-layout-exit"
				: layoutMotionPhase === "enter"
					? "sq-hex-map-character-layout-enter"
					: undefined;

		const characterZIndex = isScopedPanelSpotlight
			? SCOPED_PANEL_MAP_SPOTLIGHT_CHARACTER_Z_INDEX
			: 40;
		const playerPinZIndex = isScopedPanelSpotlight
			? SCOPED_PANEL_MAP_SPOTLIGHT_CHARACTER_PIN_Z_INDEX
			: HEXAGON_MAP_PLAYER_PIN_Z_INDEX;

		const layoutMotionStyle = isLayoutMotion
			? ({
					animationDuration: `${layoutMotionDurationMs}ms`,
				} as const)
			: undefined;

		const live = getDisplaySnapshot();
		const renderDisplay: CharacterMovementDisplaySnapshot = {
			...live,
			isMoving: coarse.isMoving,
			speedScale: coarse.speedScale,
			playerIcon: coarse.playerIcon,
			facingRad: coarse.facingRad,
			center,
		};

		const renderArgs: HexagonMapPlayerCharacterRenderArgs = {
			display: renderDisplay,
			center,
			stopOverlaySize,
			contentMapScale,
			userZoom,
			playerPinSrc,
			isScopedPanelSpotlight,
			hidePlayerPin,
			layoutMotionPhase,
			layoutMotionDurationMs,
		};

		return (
			<>
				{playerPinSrc && !hidePlayerPin ? (
					<div
						ref={pinHostRef}
						{...(isScopedPanelSpotlight
							? {
									[SCOPED_PANEL_SPOTLIGHT_DATA_ATTR]:
										"character-pin",
								}
							: {})}
						className={cn(
							"pointer-events-none absolute",
							isScopedPanelSpotlight &&
								scopedPanelSpotlightClassName,
						)}
						style={{
							zIndex: playerPinZIndex,
							left: center.left,
							top: pinAnchorTop,
							transform: `translate(-50%, -100%) scale(${inverseScale})`,
							transformOrigin: "center bottom",
							transition: positionTransition,
						}}
					>
						<div
							className={cn(layoutMotionClass)}
							style={layoutMotionStyle}
						>
							<PulseFloatMotion
								durationSec={
									HEXAGON_MAP_OVERLAY_FLOAT_DURATION_S
								}
							>
								<Image
									src={playerPinSrc}
									alt=""
									width={HEXAGON_MAP_PLAYER_PIN_WIDTH_PX}
									height={HEXAGON_MAP_PLAYER_PIN_HEIGHT_PX}
									className="block object-contain"
									style={{
										width: HEXAGON_MAP_PLAYER_PIN_WIDTH_PX,
										height: HEXAGON_MAP_PLAYER_PIN_HEIGHT_PX,
									}}
									priority
								/>
							</PulseFloatMotion>
						</div>
					</div>
				) : null}
				<div
					ref={characterHostRef}
					{...(isScopedPanelSpotlight
						? { [SCOPED_PANEL_SPOTLIGHT_DATA_ATTR]: "character" }
						: {})}
					className={cn(
						"pointer-events-none absolute flex -translate-x-1/2 -translate-y-full items-center justify-center overflow-visible transition-opacity duration-300",
						isScopedPanelSpotlight && scopedPanelSpotlightClassName,
					)}
					style={{
						zIndex: characterZIndex,
						left: center.left,
						top: feetTop,
						width: stopOverlaySize,
						height: stopOverlaySize,
						transition: positionTransition,
					}}
					data-hex-character-feet-pin=""
				>
					<div
						className={cn(
							"relative size-full overflow-visible",
							layoutMotionClass,
						)}
						style={layoutMotionStyle}
					>
						{renderPlayerCharacter(renderArgs)}
					</div>
				</div>
			</>
		);
	},
);
